import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle, XCircle, MailX } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useTranslation } from "react-i18next";

type UnsubscribeStatus = "ready" | "processing" | "success" | "already" | "error" | "invalid";

const Unsubscribe = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<UnsubscribeStatus>("ready");

  const userId = searchParams.get("user");
  const token = searchParams.get("token");

  useEffect(() => {
    // Validate that both userId and token are present
    if (!userId || !token) {
      setStatus("invalid");
    }
  }, [userId, token]);

  const handleUnsubscribe = async () => {
    if (!userId || !token) {
      setStatus("invalid");
      return;
    }

    setStatus("processing");

    try {
      const { data, error } = await supabase.functions.invoke("unsubscribe", {
        body: { userId, token },
      });

      if (error) {
        console.error("Unsubscribe error:", error);
        setStatus("error");
        return;
      }

      if (data?.status === "already_unsubscribed") {
        setStatus("already");
      } else if (data?.success) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Unsubscribe error:", error);
      setStatus("error");
    }
  };

  const renderContent = () => {
    switch (status) {
      case "ready":
        return (
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-4 bg-yellow-500/10 rounded-full w-fit">
                <MailX className="w-12 h-12 text-yellow-500" />
              </div>
              <CardTitle className="text-2xl font-display text-primary">
                {t('unsubscribe.title')}
              </CardTitle>
              <CardDescription>
                {t('unsubscribe.confirmMessage')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={handleUnsubscribe}
                className="w-full"
                variant="destructive"
              >
                {t('unsubscribe.confirm')}
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                {t('unsubscribe.note')}
              </p>
            </CardContent>
          </Card>
        );

      case "processing":
        return (
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="w-12 h-12 animate-spin text-primary" />
                <p className="text-muted-foreground">{t('unsubscribe.processing')}</p>
              </div>
            </CardContent>
          </Card>
        );

      case "success":
        return (
          <Card className="w-full max-w-md text-center">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-green-500/10 rounded-full">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold">{t('unsubscribe.successTitle')}</h2>
                <p className="text-muted-foreground">
                  {t('unsubscribe.successMessage')}
                </p>
              </div>
            </CardContent>
          </Card>
        );

      case "already":
        return (
          <Card className="w-full max-w-md text-center">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-blue-500/10 rounded-full">
                  <CheckCircle className="w-12 h-12 text-blue-500" />
                </div>
                <h2 className="text-2xl font-bold">{t('unsubscribe.alreadyTitle')}</h2>
                <p className="text-muted-foreground">
                  {t('unsubscribe.alreadyMessage')}
                </p>
              </div>
            </CardContent>
          </Card>
        );

      case "invalid":
      case "error":
        return (
          <Card className="w-full max-w-md text-center">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-destructive/10 rounded-full">
                  <XCircle className="w-12 h-12 text-destructive" />
                </div>
                <h2 className="text-2xl font-bold">{t('unsubscribe.errorTitle')}</h2>
                <p className="text-muted-foreground">
                  {status === "invalid" 
                    ? t('unsubscribe.invalidLink') 
                    : t('unsubscribe.errorMessage')}
                </p>
              </div>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <Layout>
      <SEO 
        title={t('unsubscribe.seoTitle')}
        description={t('unsubscribe.seoDescription')}
      />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 py-20 px-4">
        {renderContent()}
      </div>
    </Layout>
  );
};

export default Unsubscribe;